/* CBD HUT — Trust Icons (Flickity + swipe fallback + autoplay + dots) */
(() => {
  const SELECTOR = '[data-cbh-trust-icons]';
  const MOBILE_MQ = '(max-width: 786px)';

  const reducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const onMobile = () =>
    window.matchMedia && window.matchMedia(MOBILE_MQ).matches;

  function parseFlickityJson(el) {
    try { return JSON.parse(el.getAttribute('data-flickity') || '{}'); }
    catch (_) { return {}; }
  }

  function buildDots(root, count) {
    const dotsWrap = root.querySelector('[data-trust-dots]');
    if (!dotsWrap) return null;

    dotsWrap.innerHTML = '';
    if (count <= 1) return dotsWrap;

    for (let i = 0; i < count; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cbh-trust__dot';
      b.setAttribute('aria-label', `Go to item ${i + 1}`);
      if (i === 0) b.setAttribute('aria-current', 'true');
      dotsWrap.appendChild(b);
    }
    return dotsWrap;
  }

  function setActiveDot(dotsWrap, idx) {
    if (!dotsWrap) return;
    const dots = Array.from(dotsWrap.querySelectorAll('.cbh-trust__dot'));
    dots.forEach((d, i) => {
      if (i === idx) d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
    });
  }

  function initFallback(root, track) {
    if (track.dataset.cbhTrustFallback === '1') return;
    if (!onMobile()) return;

    track.dataset.cbhTrustFallback = '1';

    const autoplay = root.dataset.autoplay === 'true' && !reducedMotion();
    const autoplayMs = Math.max(750, Number(root.dataset.autoplayMs || 0) || 0);
    const wrap = root.dataset.wrap !== 'false';
    const showDots = root.dataset.dots !== 'false';

    const items = Array.from(track.querySelectorAll('.cbh-trust__item'));
    const dotsWrap = showDots ? buildDots(root, items.length) : null;

    const getGap = () => {
      const cs = getComputedStyle(track);
      return parseFloat(cs.gap || cs.columnGap || '0') || 0;
    };

    const getStep = () => {
      const first = items[0];
      if (!first) return track.clientWidth;
      return first.getBoundingClientRect().width + getGap();
    };

    const getIndex = () => {
      const step = getStep();
      if (!step) return 0;
      return Math.max(0, Math.min(items.length - 1, Math.round(track.scrollLeft / step)));
    };

    const scrollToIndex = (i) => {
      const step = getStep();
      track.scrollTo({ left: i * step, behavior: 'smooth' });
    };

    const update = () => setActiveDot(dotsWrap, getIndex());

    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    update();

    if (dotsWrap) {
      dotsWrap.addEventListener('click', (e) => {
        const btn = e.target.closest('.cbh-trust__dot');
        if (!btn) return;
        const dots = Array.from(dotsWrap.querySelectorAll('.cbh-trust__dot'));
        const i = dots.indexOf(btn);
        if (i >= 0) scrollToIndex(i);
      });
    }

    // Autoplay
    let timer = null;
    const start = () => {
      if (!autoplay || timer || items.length <= 1) return;
      timer = window.setInterval(() => {
        if (!onMobile()) return;
        const i = getIndex();
        const next = i + 1;
        if (next >= items.length) {
          if (wrap) scrollToIndex(0);
        } else {
          scrollToIndex(next);
        }
      }, autoplayMs);
    };
    const stop = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };

    const pauseOnHover = root.dataset.pausehover !== 'false';
    if (pauseOnHover) {
      track.addEventListener('pointerenter', stop);
      track.addEventListener('pointerleave', start);
      track.addEventListener('focusin', stop);
      track.addEventListener('focusout', start);
    }

    const onResize = () => {
      stop();
      update();
      start();
    };
    window.addEventListener('resize', onResize);

    track._cbhFallbackCleanup = () => {
      stop();
      window.removeEventListener('resize', onResize);
      delete track._cbhFallbackCleanup;
      delete track.dataset.cbhTrustFallback;
    };

    start();
  }

  function initFlickity(track) {
    if (!window.Flickity) return false;
    if (typeof window.Flickity.data === 'function' && window.Flickity.data(track)) return true;

    const opts = parseFlickityJson(track);
    // Respect reduced motion
    if (reducedMotion()) opts.autoPlay = false;

    track._cbhFlickity = new window.Flickity(track, opts);
    return true;
  }

  function initOne(root) {
    const track = root.querySelector('.js-flickity');
    if (!track) return;

    // Try Flickity
    const has = initFlickity(track);

    // If Flickity didn't enable, fallback
    if (!has && !track.classList.contains('flickity-enabled')) {
      initFallback(root, track);
    }
  }

  function boot(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach(initOne);
  }

  // Initial
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  // Theme editor
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target;
    if (!section) return;
    section.querySelectorAll(SELECTOR).forEach((root) => {
      const track = root.querySelector('.js-flickity');
      if (!track) return;

      if (track._cbhFlickity && typeof track._cbhFlickity.destroy === 'function') {
        track._cbhFlickity.destroy();
        delete track._cbhFlickity;
      }
      if (track._cbhFallbackCleanup) {
        track._cbhFallbackCleanup();
      }
    });
  });
})();
